import Box from '@mui/material/Box'
import { useRootLoaderData } from 'components/Root.loader'
import Input from 'components/ui/FormInput'
import MultiSelect from 'components/ui/MultiSelect'
import { AUTHORITIES_JP, ORDERED_AUTHORITIES } from 'config'
import { ReactNode } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import applyErrorMsg from 'utils/applyErrorMsg'
import { EMAIL, NO_SPACE_AROUND } from 'utils/regex'

import RoleDescription from './RoleDescription'
import { useLoaderData } from './UsersConfig.loader'

import type { Authority } from 'types'

export type FormData = {
  userID: number
  name: string
  email: string
  concurID: string
  authority: Authority
  subGroups: string[]
}

export type AddFormData = Omit<FormData, 'userID'>

export interface UserFormFieldsProps {
  editMode?: boolean
}

/**
 * Form fields of user. A FormProvider is required
 */
export default function UserFormFields({ editMode }: UserFormFieldsProps) {
  const { useConcur } = useRootLoaderData().userGroupConfig

  return (
    <>
      <Box maxWidth={800}>
        {editMode && (
          <Input
            type="hidden"
            name="userID"
            rules={{
              required: true,
            }}
          />
        )}
        <Field label="利用者名">
          <Input
            type="text"
            name="name"
            rules={applyErrorMsg({
              required: true,
              pattern: { value: NO_SPACE_AROUND, message: '無効な入力です' },
            })}
            testid="username"
          />
        </Field>

        <Box display="flex">
          <Field label="ビジネスID">
            <Input
              type="text"
              name="email"
              disabled={editMode}
              rules={applyErrorMsg({
                required: true,
                pattern: { value: EMAIL, message: '無効な入力です' },
              })}
              testid="line-business-id"
            />
          </Field>
          {useConcur && (
            <Field label="Concur account">
              <Input
                type="text"
                name="concurID"
                disabled={editMode}
                rules={applyErrorMsg({
                  required: true,
                  pattern: { value: EMAIL, message: '無効な入力です' },
                })}
                testid="concur-account"
              />
            </Field>
          )}
        </Box>

        <Field label="保存グループ">
          <GroupSelect />
        </Field>

        <Field label="権限の種類">
          <Input
            type="select"
            name="authority"
            rules={{ required: true }}
            options={ORDERED_AUTHORITIES.map((auth) => ({
              label: AUTHORITIES_JP[auth],
              value: auth,
            }))}
            testid="authority-type"
          />
        </Field>
      </Box>
      <RoleDescription />
    </>
  )
}

interface FieldProps {
  label: string
  children?: ReactNode
}
function Field({ label, children }: FieldProps) {
  return (
    <Box width="50%" paddingTop={1} paddingBottom={1} paddingRight={2}>
      <label>
        <Box paddingBottom={1}>{label}</Box>
        {children}
      </label>
    </Box>
  )
}

function GroupSelect() {
  const { control } = useFormContext()
  const usersGroups = useLoaderData()
  const subGroupNames = usersGroups.subGroups.map(({ name }) => name)

  return (
    <Controller
      control={control}
      name="subGroups"
      render={({ field: { onChange, value } }) => (
        <MultiSelect
          options={subGroupNames}
          onChange={(e, val) => {
            onChange(val)
          }}
          value={value === undefined ? [] : value}
          data-testid="save-groups"
        />
      )}
    />
  )
}
